/**
 * Shared types for the Reluna Copilot Agent Constructor feature (doc 09 §1 UI).
 * View components and the shell both import from here so the contract is one
 * place. Re-exports the runtime DTO union types the views render against.
 */

import {
  AgentStakesDTO,
  AgentTriggerDTO,
  AutonomyLevelDTO,
  BudgetDTO,
  FieldClassDTO,
  ModelClassDTO,
  ReadinessFeedStateDTO,
  TaskCycleStateDTO,
  WorkItemDTO,
} from '@dto/gateway-secured/models';
import { IconNames } from '@shared/constants/icons';
import { Authority } from '@shared/types/authority';

/**
 * One readiness feed row the shell renders (derived from the GET /readiness
 * snapshot's keyed feeds map — the shell projects it into an ordered list for
 * display). `labelKey` is the i18n key for the feed's name; `state` is its health.
 */
export interface ReadinessFeedRow {
  labelKey: string;
  state: ReadinessFeedStateDTO;
}

/**
 * The top-level views the container switches between (doc 09 §1 + CIO wave).
 * Ask limits is not a view of its own: it is a section of the Settings view
 * (same AI_AGENT_ADMIN gate), so it has no key here.
 */
export type TabKey =
  | 'overview'
  | 'ask'
  | 'registry'
  | 'runs'
  | 'recommendations'
  | 'reports'
  | 'alerts'
  | 'approvals'
  | 'governance'
  | 'settings';

/** The sidebar groups the container's views are listed under, in display order. */
export type CopilotNavGroupKey = 'overviewChat' | 'work' | 'manage';

/** One rendered sidebar group: its i18n title and the visible views under it. */
export interface CopilotNavGroup {
  key: CopilotNavGroupKey;
  /** i18n key for the group title (pages.copilot.navGroup.*). */
  labelKey: string;
  tabs: readonly CopilotTab[];
}

export enum CopilotPollView {
  Runs = 'runs',
  Approvals = 'approvals',
  Governance = 'governance',
  Other = 'other',
}

export enum CopilotPollResource {
  Runs = 'runs',
  Approvals = 'approvals',
}

/** One entry in the container sub-nav. `authority` gates visibility (doc 09 §7). */
export interface CopilotTab {
  key: TabKey;
  /** i18n key for the tab label (pages.copilot.nav.*). */
  labelKey: string;
  /** 16px sidebar icon (.project#1877). Presentation only — never part of any gate. */
  icon?: IconNames;
  /**
   * The authority that gates this tab's visibility (doc 09 §7). When set, the tab
   * is shown only if the user holds it (UserState.hasAuthorities). Undefined tabs
   * are always shown. The Ask tab is gated on AI_ASSISTANT_ASK.
   */
  authority?: Authority;
  /**
   * One-of read authorities for tabs whose rows span several agent lanes. Unlike
   * `entryAuthorities`, these rights actually authorize loading/rendering the
   * tab's data. RUN/APPROVE rights never imply read access.
   */
  viewAuthorities?: readonly Authority[];
  /**
   * E8 (review #5): the ADDITIONAL authorities that make the route worth opening —
   * the write/manage/run/approve surfaces that live INSIDE a readable tab. They
   * do NOT grant read access; inner controls enforce them independently. They exist so
   * COPILOT_ENTRY_AUTHORITIES — the /copilot route guard's and navbar item's
   * one_of list — is the UNION of everything a tab can be useful for. Without
   * this, entry drifted narrower than the tab set and locked entitled users out.
   */
  entryAuthorities?: readonly Authority[];
}

/**
 * One agent the Trigger Run panel offers (CIO wave). DERIVED from the live
 * GET /agents catalog — every agent that is ENABLED for the tenant and whose
 * RUN authority the user holds is runnable (replacing the old hardcoded
 * RUN_TRIGGER_AGENTS pair). `label` is the contract displayName, else the id.
 */
export interface TriggerAgentChoice {
  /** The wired runtime agent id (agent = code, doc 09 §2). */
  id: string;
  /** Human-readable label (displayName ?? id — runtime data, not an i18n key). */
  label: string;
}

/**
 * A failed runtime call classified into the honest onboarding hint the UI
 * renders (CIO wave §hints): 'forbidden' = 403 missing authority (`authority`
 * names it — the Permission Center hint), 'not-entitled' = the tenant lacks
 * the agent's AI_AGENT_* entitlement (the enable-toggle hint), 'not-found' =
 * an honest 404 (usually a valid empty state), 'conflict' = a 409 (e.g.
 * maker-checker / not-active), 'unreachable' = transport failure / timeout /
 * 5xx (the runtime-down banner), 'error' = anything else.
 */
export interface RuntimeCallError {
  kind: 'forbidden' | 'not-entitled' | 'not-found' | 'conflict' | 'unreachable' | 'error';
  /** The missing AI_AGENT_* authority when kind === 'forbidden' and it is known. */
  authority?: string;
  /** The runtime's verbatim error detail, when present. */
  detail?: string;
}

/**
 * Failure detail returned while a custom-agent contract is saved. Runtime
 * validation uses one array entry per authored `errors[]` item so the dialog
 * can render every line verbatim; non-validation and transport failures keep
 * their single safe text message.
 */
export type AgentSaveErrorDetail = string | readonly string[];

/**
 * The optimistic-lock loss on PUT /agents/:id (HTTP 409): SOMEBODY ELSE SAVED
 * this agent while the form was open, so the runtime refused rather than
 * clobbering their revision. `currentVersion` is the head the agent has actually
 * reached — what reopening the form will seed from — and is absent only if the
 * runtime did not name it. Distinguished from a plain validation failure because
 * the remedy is different: nothing in the form is wrong; it was written against
 * a version that no longer exists.
 */
export interface AgentVersionConflict {
  /** The version the agent is at NOW. */
  currentVersion?: string;
  /** The stale version the refused Save declared it edited from. */
  expectedVersion: string;
}

/**
 * The LIVE work-item catalog loaded for one agent + tenant (phase 1:
 * GET /agents/:id/work-items). When the load fails (`error`) or comes back
 * empty, the pickers show that state honestly and offer ONLY the
 * "Default (first available)" option — there is deliberately no fixture
 * fallback (the fixture ids were removed from the runtime and 404 on POST /runs).
 */
export interface WorkItemCatalogState {
  items: WorkItemDTO[];
  loading: boolean;
  error: boolean;
  /**
   * The runtime's structural reason for an empty catalog (night build
   * ai/night-2026-07-08: GET /agents/:id/work-items now returns { items, reason? }).
   * e.g. 'no rulebook uploaded', 'no open work items'. Undefined when there are
   * items or no reason was supplied; the pickers surface it in the empty state.
   */
  reason?: string;
}

/**
 * What the run list / run header render for ONE work item. `named` records
 * WHICH tier produced `text`: a resolved platform name or a catalog description
 * (`true`), or nothing at all but the id itself (`false` — the view then says so
 * instead of printing a bare UUID that reads like an unrecognised name).
 */
export interface WorkItemLabelView {
  /** The work item's raw id, always available for the tooltip. */
  id: string;
  /** The label to show: a name, a description, or the id when nothing named it. */
  text: string;
  /** True only when a real name or description was found. */
  named: boolean;
}

/**
 * A row in the task-cycle timeline (doc 09 §3). The shell exposes the canonical
 * ordering (TASK_CYCLE_ORDER) so the run viewer can render past/current/future
 * states even before their RunEvents arrive.
 */
export interface TimelineStep {
  state: TaskCycleStateDTO;
  /** i18n key for the state label (pages.copilot.runViewer.states.*). */
  labelKey: string;
  /** i18n key for the plain-language hint explaining the state (pages.copilot.hint.state.*). */
  hintKey: string;
}

/** Visual status of a timeline step relative to the run's currentState. */
export type TimelineStepStatus = 'done' | 'current' | 'pending' | 'aborted';

/**
 * A selectable option in the constructor form (autonomy/trigger/stakes/model/
 * memory). Backed by an i18n label key so all user-facing text stays translated.
 */
export interface ContractFormOption<T extends string = string> {
  value: T;
  labelKey: string;
  /**
   * When true, the option is shown but NOT selectable ("coming soon"). Used by
   * the tool picker: only tools on the backend KNOWN_TOOLS allowlist can be sent
   * in `contract.tools[]`; POST /agents/create 400s on any other tool id, so the
   * unwired options are surfaced honestly as disabled rather than removed.
   */
  comingSoon?: boolean;
}

/** Constructor option with helper copy shown under the selected value. */
export interface DescribedContractFormOption<T extends string = string>
  extends ContractFormOption<T> {
  descriptionKey: string;
}

/** Preset key for user-friendly budget selection before showing hard caps. */
export type BudgetPresetKey = 'guarded' | 'standard' | 'extended';

/** A named budget preset plus the hard runtime caps it applies. */
export interface BudgetPresetOption extends DescribedContractFormOption<BudgetPresetKey> {
  budget: BudgetDTO;
}

/** A product/module preset that maps business intent to the runtime contract. */
export interface AgentModuleOption extends DescribedContractFormOption {
  layer: number;
  authority: string;
  defaultTrigger: AgentTriggerDTO;
  defaultTools: string[];
  /**
   * The memory layers this module's agents may READ, as backend layer names
   * (working | episodic | semantic | procedural) — the contract's real shape,
   * not a preset. Empty = reads nothing between runs.
   */
  defaultMemoryRead: readonly string[];
  /**
   * The memory layers this module's agents may WRITE. Stated separately and
   * ALWAYS sent, because the old presets hid this: three of them wrote nothing
   * and one silently inherited the server default `write: ['procedural']`.
   */
  defaultMemoryWrite: readonly string[];
  defaultModelClass: ModelClassDTO;
  defaultBudgetPreset: BudgetPresetKey;
  defaultAutonomyLevel: AutonomyLevelDTO;
  defaultStakes: AgentStakesDTO;
  defaultClientFacing: boolean;
  defaultCrossModel: boolean;
  /**
   * Mission text the constructor seeds while the mission control is pristine
   * (phase 3). Contract PAYLOAD (sent to the runtime), not UI copy — hence a
   * plain string like the schedule/reviewers seeds, not an i18n key. Empty for
   * the 'custom' module so the author must write their own.
   */
  defaultMission: string;
}

/** Field-class color-map key (doc 09 §5) — maps a class to a theme color token. */
export type FieldClassColorKey = FieldClassDTO;

/**
 * A calendar-day bucket of timestamped rows for the governance audit / egress
 * logs (doc 09 §7). Rows arrive as one flat, unsorted array; the view sorts them
 * newest-first, slices a bounded page, then groups the page into these buckets so
 * a day header separates each group. `dayKey` is a stable local-date key
 * (YYYY-MM-DD) for @for tracking; `dayTimestamp` is the ISO timestamp of the
 * day's newest row, formatted in the template for the header. `rows` is already
 * sorted newest-first within the day.
 */
export interface DayGroup<T> {
  dayKey: string;
  dayTimestamp: string;
  rows: T[];
}

/** The memory KEYS one layer exposed to a run ('memory-context' artifact — keys only, never values). */
export interface CommitteeMemoryLayer {
  layer: string;
  keys: string[];
}

/** What the committee recalled from episodic memory (bounded strings, C3-safe by design). */
export interface CommitteeMemoryRecall {
  /** True whenever the memory.recall dispatch ran (it always does). */
  consulted: boolean;
  /** True when the tool answered entries; false = honest empty memory. */
  resolved: boolean;
  outcomes: { key: string; disposition: string }[];
}

/**
 * The Governance Memory panel's view (CIO wave 2): derived from the LAST
 * investment-committee run via the EXISTING run endpoints, because the runtime
 * exposes no memory-browsing endpoint yet (an honest fallback, noted as a
 * runtime follow-up). `runId` null = the tenant has no committee run yet.
 */
export interface CommitteeMemoryView {
  runId: string | null;
  /** ISO-8601 start of the run the view derives from. */
  startedAt?: string;
  /** Per-layer memory keys the run perceived ('memory-context', keys only). */
  memoryLayers: CommitteeMemoryLayer[];
  /** The recall record off the run output, or null when unreadable/not committed. */
  recall: CommitteeMemoryRecall | null;
}

/**
 * Friendly cause of a failed POST /runs (night build adds the first two 429
 * causes; the CIO wave adds the two 403 onboarding causes). Drives which toast
 * the run trigger surfaces: 'not-entitled' = the tenant's entitlement for the
 * agent's authority is off (the enable-toggle hint), 'forbidden' = the USER
 * lacks the RUN authority (the Permission Center hint).
 */
export type StartRunErrorKind =
  | 'monthly-token-budget'
  | 'concurrency-cap'
  | 'rate-limit'
  | 'not-entitled'
  | 'forbidden'
  /**
   * THERE WAS NOTHING TO RUN. POST /runs answers 400 and puts the runtime's own
   * explanation in a MACHINE-READABLE `notRunnableReason` field beside the
   * prose — "so a FE need never parse prose" (app.ts workItemRefusal). This kind
   * is set from the presence of that field, never from the message text.
   */
  | 'no-work-item'
  | 'error';

/**
 * WHETHER A RUN CAN BE STARTED AT ALL, and why not.
 *
 * The Run button used to be enabled whenever an agent was selected, so on a
 * tenant whose work-item universe is empty the only way to discover there was
 * nothing to run was to press it and read a toast. The runtime already answers
 * the question BEFORE the press: `GET /agents/:id/work-items` returns
 * `{ items: [], reason? }`, and POST /runs resolves the very same list under the
 * very same caller scope (`wiring.listWorkItemDetails` -> `defaultWorkItem`), so
 * an empty catalog and a refused launch are the SAME server fact.
 */
export interface RunAffordance {
  /** True when a launch is certain to be refused, so the control is disabled. */
  blocked: boolean;
  /**
   * The runtime's own words for why, VERBATIM (the catalog's structural
   * `reason` — the identical string the 400 carries as `notRunnableReason`),
   * or null when the catalog gave none. Never composed here.
   */
  reason: string | null;
}
